import { Close } from '@mui/icons-material';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';

import { useUserStore } from '@/configs/store';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { getColorByMinutes } from '@/utils/getColorByMinutes';

import { useTimeStatsModel } from '../TimeStatsModel';
import { useGlobalUserFilterModel } from '../globalUserFilter';
import { viewSettingsStore } from '../viewSettings';

import { getChartColorByIndex } from './constants';
import { ProjectDistributionChart } from './ui/ProjectDistributionChart';
import { TimeEstimationScatterChart } from './ui/TimeEstimationScatterChart';
import { TimePieChart } from './ui/TimePieChart';

import s from './TimeStatsDialog.module.scss';

type TimeStatsDialogProps = {
  open: boolean;
  handleDialogClose: () => void;
};

export const TimeStatsDialog: React.FC<TimeStatsDialogProps> = observer(
  ({ open, handleDialogClose }) => {
    const {
      logChartData,
      totalTime,
      logMinutes,
      draftMinutes,
      estimationPoints,
      estimationFailedCount,
      estimationSkippedCount,
      estimationLoadingStage,
      statsPeriod,
      loadEstimationVariance,
    } = useTimeStatsModel();
    const { workTime, view } = viewSettingsStore;
    const { currentDate } = useTimeLogStore();
    const { user } = useUserStore();
    const { selectedUser, selectedUserId } = useGlobalUserFilterModel();
    const myselfWorkTime = user?.id === selectedUser?.uid ? Number(workTime) : 40;

    useEffect(() => {
      if (!open) {
        return;
      }

      void loadEstimationVariance();
    }, [open, currentDate, selectedUserId, view, loadEstimationVariance]);

    const colorByMinutes = useMemo(
      () => getColorByMinutes(totalTime, myselfWorkTime, view, currentDate),
      [totalTime, myselfWorkTime, view, currentDate],
    );

    return (
      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullWidth
        maxWidth={false}
        sx={{
          '.MuiDialog-container': { width: '100%' },
          '.MuiPaper-root': { width: '70%', borderRadius: '8px' },
        }}
      >
        <div className={s.dialog__header}>
          <h1 className={s.dialog__title}>Статистика</h1>
          <IconButton onClick={handleDialogClose} size="small">
            <Close />
          </IconButton>
        </div>
        <h2 className={s.dialog__total_time} style={{ color: colorByMinutes }}>
          Общее время: {formatDurationRussian(totalTime)}
        </h2>
        {draftMinutes > 0 && (
          <p className={s.dialog__time_breakdown}>
            Залогировано: {formatDurationRussian(logMinutes)} · Черновики:{' '}
            {formatDurationRussian(draftMinutes)}
          </p>
        )}
        <DialogContent className={s.dialog__wrapper} tabIndex={-1} sx={{ paddingTop: 0 }}>
          <ProjectDistributionChart />
          <h2>Топ задач</h2>
          <TimePieChart
            reverse
            data={logChartData}
            getColor={(_, index) => getChartColorByIndex(index)}
          />
          <h2>Задачи: факт к оценке</h2>
          <TimeEstimationScatterChart
            points={estimationPoints}
            periodStart={statsPeriod?.start ?? null}
            periodEnd={statsPeriod?.end ?? null}
            isLoading={estimationLoadingStage.isLoading || estimationLoadingStage.isNotStarted}
            isError={
              estimationLoadingStage.isError ||
              (estimationLoadingStage.isSuccess &&
                estimationPoints.length === 0 &&
                estimationFailedCount > 0 &&
                estimationSkippedCount === 0)
            }
            failedCount={estimationFailedCount}
            skippedCount={estimationSkippedCount}
          />
        </DialogContent>
      </Dialog>
    );
  },
);
